import { Relref } from 'assets/ts/hugo';
import Big from 'big.js';
import axios from 'smg/axios';
import { Start } from 'smg/buttons';
import * as hyejoo from 'smg/customer-api/hyejoo';
import { GetPriceByArea } from 'smg/customer-api/lineitems';
import { HandleRequestError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import { GetReadableSize, GetWidthHeightFromSize } from 'smg/lineitemtypesizes';
import * as models from 'smg/models';
import { CleanNumber } from 'smg/number';
import { DollarFormat } from 'smg/util';
import * as sidebar from '../../free-quote/sidebar';
import * as store from '../../free-quote/store';

declare interface myWindow extends Window {
    dataLayer: Record<string, unknown>[];
    readonly product: string;
}
declare const window: myWindow;

if (document.body.id === 'free-quote-details') {
    main();
}

function main() {
    store.Init();

    const settings = store.Get();
    if (!settings.getStarted) {
        store.Redirect();
        return;
    }

    const $form = document.querySelector('#free-quote-form') as HTMLFormElement;
    const $fieldset = $form.querySelector('fieldset') as HTMLFieldSetElement;

    const $shapes = document.querySelectorAll<HTMLInputElement>('[name="free-quote-shape"]');
    const $customShapeContainer = document.querySelector<HTMLElement>('#free-quote-shape-custom-container');
    const $customShape = document.querySelector<HTMLInputElement>('#free-quote-shape-custom');

    const $colors = document.querySelectorAll<HTMLInputElement>('[name="free-quote-color"]');

    const $types = document.querySelectorAll<HTMLInputElement>('[name="free-quote-type"]');

    const $sizes = document.querySelectorAll<HTMLInputElement>('[name="free-quote-size"]');
    const $customSizeContainer = document.querySelector<HTMLElement>('#free-quote-size-custom-container');
    const $customSizeWidth = document.querySelector<HTMLInputElement>('#free-quote-size-width');
    const $customSizeHeight = document.querySelector<HTMLInputElement>('#free-quote-size-height');

    const $backings = document.querySelectorAll<HTMLInputElement>('[name="free-quote-backing"]');

    const $attachments = document.querySelectorAll<HTMLInputElement>('[name="free-quote-attachment"]');

    const $qtys = document.querySelectorAll<HTMLInputElement>('[name="free-quote-qty"]');
    const $qtyDesc = document.querySelector<HTMLElement>('#free-quote-qty-desc');
    const $customQtyContainer = document.querySelector<HTMLInputElement>('#free-quote-qty-custom-container');
    const $customQty = document.querySelector<HTMLInputElement>('#free-quote-qty-custom-qty');

    const updateSubmit = () => {
        sidebar.$submit.disabled = !$form.checkValidity();
    };

    const getDetailBackingOption = () => Array.from($backings).find($backing => $backing.checked);
    const getDetailsBacking = () => getDetailBackingOption()?.value || '';

    const getDetailsAttachmentOption = () => Array.from($attachments).find($attachment => $attachment.checked);
    const getDetailsAttachment = () => getDetailsAttachmentOption()?.value || '';

    const typeDetailsGetter: store.DetailGetter = {
        getDetailsColor: () => {
            if (window.product === 'Challenge Coin' && window.location.href.includes('american-made')) {
                return Array.from($types).find($type => $type.checked)?.value || '';
            } else {
                return Array.from($colors).find($color => $color.checked)?.value || '';
            }
        },

        getDetailsWidth: () => width(),

        getInDepthType: () => {
            if (window.product === 'Challenge Coin')  {
                if (window.location.href.includes('american-made')) {
                    return Array.from($types).find($types => $types.checked)?.value || '';
                } else {
                    return Array.from($colors).find($color => $color.checked)?.value || '';
                }
            } else {
                return undefined;
            }
        },
        getInDepthLineItemTypeID: () => {
            if (window.product === 'Challenge Coin')  {
                if (window.location.href.includes('american-made')) {
                    return Array.from($types).find($types => $types.checked)?.getAttribute('data-line-item-type-id') as models.LineItemTypeID;
                } else {
                    return Array.from($colors).find($color => $color.checked)?.getAttribute('data-line-item-type-id') as models.LineItemTypeID;
                }
            } else {
                return undefined;
            }
        },

        getDetailsBacking,
        getDetailsBackingPrice: () => Big(getDetailBackingOption()?.parentElement?.querySelector('.child-line-item-option-price')?.getAttribute('data-price') || '0'),

        getDetailsAttachment,
        getDetailsAttachmentPrice: () => Big(getDetailsAttachmentOption()?.parentElement?.querySelector('.child-line-item-option-price')?.getAttribute('data-price') || '0'),
    };

    const size = (): string | undefined => {
        const $size = Array.from($sizes).find($sizes => $sizes.checked);
        if (!$size) {
            return;
        }

        if ($size.value === 'custom') {
            if (!$customSizeWidth || !$customSizeHeight) {
                return;
            }
            return GetReadableSize(parseFloat(CleanNumber($customSizeWidth?.value || '0')), parseFloat(CleanNumber($customSizeHeight?.value || '0')), '');
        }

        return $size.value;
    };

    const width = (): number => {
        const s = size();
        if (!s) {
            return 0;
        }
        const { width } = GetWidthHeightFromSize(s);
        return width;
    };

    const height = (): number => {
        const s = size();
        if (!s) {
            return 0;
        }
        const { height } = GetWidthHeightFromSize(s);
        return height;
    };

    const quantity = (): number => {
        const $qty = Array.from($qtys).find($qty => $qty.checked);
        if ($qty) {
            return parseInt(CleanNumber($qty.value === 'custom' ? $customQty?.value || '0' : $qty.value), 10);
        }
        return 0;
    };

    const getMoldFee = async (): Promise<Big> => {
        const s = size();
        if (!s) {
            return Big(0);
        }

        const { width, height } = GetWidthHeightFromSize(s);

        const { lineItemTypeID } = store.Type(typeDetailsGetter);

        let lineItemTypeSizeID: string | undefined;
        await axios.get<hyejoo.LineItemTypeSizes>('/line-item-type-sizes', {
            params: {
                'q': `lineItem:"${window.product}" lineItemTypeID:"${lineItemTypeID ?? ''}" width:${width} ${height !== width ? `height:${height}` : ''} type:"",null`,
                'data': 'id',
            },
        }).then(resp => {
            lineItemTypeSizeID = resp.data.lineItemTypeSizes[0]?.id;
        });

        let baseMoldFee = Big(0);

        await axios.get<hyejoo.ChildLineItems>('/child-line-items', {
            params: {
                'q': `lineItem:"${window.product}" mold:yes`,
                'data': 'lineItemID,networkChildLineItem,childLineItemOptions,name,pluralName',
                'width': width,
                'height': height,
                'quantity': quantity(),
                'lineItemTypeID': lineItemTypeID,
                'lineItemTypeSizeID': lineItemTypeSizeID,
            },
        }).then(resp => {
            for (const childLineItem of resp.data.childLineItems) {
                if (childLineItem.id === models.ChildLineItemID.CHILD_LINE_ITEM_PIN_MOLD_FEE) {
                    baseMoldFee = Big(childLineItem.networkChildLineItem?.price || '0');
                }

                if (childLineItem.childLineItemOptions) {
                    for (const childLineItemOption of childLineItem.childLineItemOptions) {
                        if (childLineItemOption.id === models.ChildLineItemOptionID.CHILD_LINE_ITEM_OPTION_COIN_MOLD_FEE_NEW_BOTH_SIDES) {
                            baseMoldFee = Big(childLineItemOption.networkChildLineItemOption?.price || '0');
                        }
                    }
                }
            }
        }).catch(err => {
            HandleRequestError(err);
            throw err;
        });

        return baseMoldFee;
    };

    const updateSidebar = async () => {
        const { name: typeName } = store.Type(typeDetailsGetter);
        const sizeName = size();
        const qty = quantity();

        if (!sizeName || !qty) {
            return;
        }

        if (qty) {
            sidebar.$sidebarQty.textContent = Intl.NumberFormat().format(qty);
        } else {
            sidebar.$sidebarQty.textContent = 'TBD';
        }

        let price = await GetPriceByArea(window.product, typeName, width(), height(), quantity());
        if (price.eq(0)) {
            sidebar.$sidebarUnitPrice.textContent = 'TBD';
        } else {
            price = price.add(await store.ChildrenTotalUnitPrice(typeDetailsGetter));

            sidebar.$sidebarUnitPrice.textContent = DollarFormat(price);
        }

        const moldFee = await getMoldFee();
        sidebar.$sidebarMoldFee.textContent = DollarFormat(moldFee);

        if (price.eq(0)) {
            sidebar.$sidebarTotal.textContent = 'TBD';
        } else {
            sidebar.$sidebarTotal.textContent = DollarFormat(price.times(quantity()).plus(moldFee));
        }
    };

    const updateQtyDesc = async () => {
        if (!$qtyDesc) {
            return;
        }

        const $qty = Array.from($qtys).find($qty => $qty.checked);
        if (!$qty || $qty.value === 'custom') {
            $qtyDesc.textContent = '';
            return;
        }

        if ($qty === $qtys[0]) {
            $qtyDesc.textContent = 'Minimum Viable Production Run';
            return;
        }

        const { name: typeName } = store.Type(typeDetailsGetter);
        const sizeName = size();
        if (!sizeName) {
            $qtyDesc.textContent = '';
            return;
        }

        const firstPrice = await GetPriceByArea(window.product, typeName, width(), height(), parseInt(CleanNumber($qtys[0].value), 10));
        if (firstPrice.eq(0)) {
            $qtyDesc.textContent = '';
            return;
        }
        const price = await GetPriceByArea(window.product, typeName, width(), height(), quantity());
        const diff = firstPrice.minus(price);
        const pct = diff.div(firstPrice).times(100);

        $qtyDesc.textContent = `SAVE ${pct.toFixed(0)}%, a significantly better unit price`;
    };

    $shapes.forEach($shape => {
        $shape.addEventListener('click', () => {
            if ($shape.value === 'Custom Shape') {
                $customShapeContainer?.classList.remove('d-none');
                if ($customShape) {
                    $customShape.disabled = false;
                    setTimeout(() => {
                        $customShape.focus();
                    });
                }
            } else {
                $customShapeContainer?.classList.add('d-none');
                if ($customShape) {
                    $customShape.disabled = true;
                }
            }

            void updateSidebar();
            updateSubmit();
        });
    });

    $customShape?.addEventListener('input', () => {
        void updateSidebar();
        updateSubmit();
    });

    $colors.forEach($color => {
        $color.addEventListener('click', () => {
            if ($color.value === 'yes') {
                sidebar.$previewEnamel?.classList.remove('d-none');
            } else {
                sidebar.$previewEnamel?.classList.add('d-none');
            }

            void updateQtyDesc();
            void updateSidebar();
            updateSubmit();
        });
    });

    $types.forEach($type => {
        $type.addEventListener('click', () => {
            const lineItemTypeID = $type.getAttribute('data-line-item-type-id') as models.LineItemTypeID;
            $sizes.forEach($size => {
                const requires = $size.getAttribute('data-requires');
                const requiresNot = $size.getAttribute('data-requires-not');
                if (requires || requiresNot) {
                    if (requires && !requires.includes(lineItemTypeID)) {
                        $size.checked = false;
                        $size.disabled = true;
                        $size.closest('label')?.classList.add('d-none');
                    } else {
                        $size.disabled = false;
                        $size.closest('label')?.classList.remove('d-none');
                    }
                    if (requiresNot?.includes(lineItemTypeID)) {
                        $size.checked = false;
                        $size.disabled = true;
                        $size.closest('label')?.classList.add('d-none');
                    } else {
                        $size.disabled = false;
                        $size.closest('label')?.classList.remove('d-none');
                    }
                }
            });
            if ($type.getAttribute('data-default-finish')) {
                sidebar.SetPlating($type.getAttribute('data-default-finish') ?? '');
            }
            if ($type.getAttribute('data-color')) {
                sidebar.SetUpgrade('', $type.getAttribute('data-color') ?? '');
            } else {
                sidebar.SetUpgrade('', 'no');
            }
            void updateQtyDesc();
            void updateSidebar();
            updateSubmit();
        });
    });

    $sizes.forEach($size => {
        $size.addEventListener('click', () => {
            if ($size.value === 'custom') {
                $customSizeContainer?.classList.remove('d-none');
                if ($customSizeWidth) {
                    $customSizeWidth.disabled = false;
                    $customSizeWidth.focus();
                }
                if ($customSizeHeight) {
                    $customSizeHeight.disabled = false;
                }
            } else {
                $customSizeContainer?.classList.add('d-none');
                if ($customSizeWidth) {
                    $customSizeWidth.disabled = true;
                }
                if ($customSizeHeight) {
                    $customSizeHeight.disabled = true;
                }
            }

            void updateQtyDesc();
            void updateSidebar();
            updateSubmit();
        });
    });

    $customSizeWidth?.addEventListener('input', () => {
        void updateQtyDesc();
        void updateSidebar();
        updateSubmit();
    });
    $customSizeHeight?.addEventListener('input', () => {
        void updateQtyDesc();
        void updateSidebar();
        updateSubmit();
    });

    $backings.forEach($backing => {
        $backing.addEventListener('click', () => {
            void updateSidebar();
            updateSubmit();
        });
    });

    $attachments.forEach($attachment => {
        $attachment.addEventListener('click', () => {
            void updateSidebar();
            updateSubmit();
        });
    });

    $qtys.forEach($qty => {
        $qty.addEventListener('click', () => {
            if ($qty.value === 'custom') {
                $customQtyContainer?.classList.remove('d-none');
                if ($customQty) {
                    $customQty.disabled = false;
                    setTimeout(() => {
                        $customQty.focus();
                    });
                }
            } else {
                $customQtyContainer?.classList.add('d-none');
                if ($customQty) {
                    $customQty.disabled = true;
                }
            }

            void updateQtyDesc();
            void updateSidebar();
            updateSubmit();
        });
    });

    $customQty?.addEventListener('input', () => {
        void updateQtyDesc();
        void updateSidebar();
        updateSubmit();
    });

    if (settings.details) {
        if (settings.details.shape) {
            const shape = settings.details.shape;
            const $shape = Array.from($shapes).find($shape => $shape.value === shape);
            if ($shape) {
                $shape.click();
                $shape.dispatchEvent(new Event('click'));
            }
        }

        if (settings.details.customShape && $customShape) {
            $customShape.value = settings.details.customShape;
        }

        if (settings.details.color) {
            const color = settings.details.color;
            const $color = Array.from($colors).find($color => $color.value === color);
            if ($color) {
                $color.click();
                $color.dispatchEvent(new Event('click'));
            }
        }

        if (settings.details.lineItemTypeID) {
            const lineItemTypeID = settings.details.lineItemTypeID;
            const $type = Array.from($types).find($type => $type.getAttribute('data-line-item-type-id') === lineItemTypeID);
            if ($type) {
                $type.click();
                $type.dispatchEvent(new Event('click'));
            }
        }

        if (settings.details.size) {
            const size = settings.details.size;
            const $size = Array.from($sizes).find($size => $size.value === size);
            if ($size) {
                $size.click();
                $size.dispatchEvent(new Event('click'));
            }
        }

        if (settings.details.customWidth && $customSizeWidth) {
            $customSizeWidth.value = settings.details.customWidth.toString();
        }
        if (settings.details.customHeight && $customSizeHeight) {
            $customSizeHeight.value = settings.details.customHeight.toString();
        }

        if (settings.details.backing) {
            const backing = settings.details.backing;
            const $backing = Array.from($backings).find($backing => $backing.value === backing);
            if ($backing) {
                $backing.click();
                $backing.dispatchEvent(new Event('click'));
            }
        }

        if (settings.details.attachment) {
            const attachment = settings.details.attachment;
            const $attachment = Array.from($attachments).find($attachment => $attachment.value === attachment);
            if ($attachment) {
                $attachment.click();
                $attachment.dispatchEvent(new Event('click'));
            }
        }

        if (settings.details.qty) {
            const qty = settings.details.qty;
            const $qty = Array.from($qtys).find($qty => $qty.value === qty.toString());
            if ($qty) {
                $qty.click();
                $qty.dispatchEvent(new Event('click'));
            }
        }

        if (settings.details.customQty && $customQty) {
            $customQty.value = settings.details.customQty.toString();
        }
    } else {
        let defaultPlating = '';
        if (window.location.href.includes('american-made')) {
            defaultPlating = 'Antique Brass';
        }
        sidebar.SetPlating(defaultPlating);
    }

    sidebar.$submit.addEventListener('click', () => {
        if (!ReportValidity($form)) {
            return;
        }

        Start(sidebar.$submit);
        $fieldset.disabled = true;

        const settings = store.Get();

        let lineItemTypeID = Array.from($colors).find($color => $color.checked)?.getAttribute('data-line-item-type-id') as models.LineItemTypeID;
        let typeName;

        if (window.product === 'Challenge Coin') {
            if (window.location.href.includes('american-made')) {
                lineItemTypeID = Array.from($types).find($type => $type.checked)?.getAttribute('data-line-item-type-id') as models.LineItemTypeID;
                typeName = Array.from($types).find($type => $type.checked)?.value || '';
            }
        }

        let color = Array.from($colors).find($color => $color.checked)?.value || '';
        if (window.product === 'Challenge Coin' && window.location.href.includes('american-made')) {
            color = Array.from($types).find($type => $type.checked)?.getAttribute('data-color') || '';
        }

        settings.details = {
            shape: Array.from($shapes).find($shape => $shape.checked)?.value || '',
            customShape: $customShape?.disabled === false ? $customShape?.value || '' : '',

            color,
            lineItemTypeID,
            type: typeName,

            size: Array.from($sizes).find($size => $size.checked)?.value || '',
            customWidth: parseFloat(CleanNumber($customSizeWidth?.value || '0')),
            customHeight: parseFloat(CleanNumber($customSizeHeight?.value || '0')),

            backing: getDetailsBacking(),

            attachment: getDetailsAttachment(),

            qty: Array.from($qtys).find($qty => $qty.checked)?.value || '',
            customQty: parseInt(CleanNumber($customQty?.value || '0'), 10),

            unitPrice: sidebar.$sidebarUnitPrice?.textContent || '',
            moldFee: sidebar.$sidebarMoldFee?.textContent || '',
            total: sidebar.$sidebarTotal?.textContent || '',
        };
        store.Set(settings);

        if (window.location.href.includes('american-made')) {
            window.location.href = Relref('/american-made-free-quote/upload');
        } else {
            window.location.href = Relref('/free-quote/upload');
        }
    });

    const $spinner = document.querySelector('#free-quote-spinner-parent') as HTMLElement;
    const $content = document.querySelector('#free-quote-content') as HTMLElement;
    $spinner.remove();
    $content.classList.remove('d-none');
}
